import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, MapPin, User, ChevronRight, Calendar as CalendarIcon, Navigation, ClipboardList } from "lucide-react";

import InstallerShell from "@/components/installer/InstallerShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { api } from "@/lib/api";
import { openDirections, resolveMapAddress } from "@/lib/directions";

/**
 * DEV-006B · Dedicated Engineer Upcoming Visits list.
 *
 * Replaces the previous dashboard drill-down that dropped the engineer on
 * today's Calendar view (which is often empty when the next visit is weeks
 * away). This page uses the SAME canonical source Mobile uses:
 *   • Identity  : `GET /api/engineers/me/workload` → `engineer_id`
 *   • Visits    : `GET /api/calendar/appointments?engineer_id=<me>&from=<now>`
 *   • Client-side rules match Mobile exactly — non-cancelled/completed only,
 *     starts >= now, chronological order. This guarantees the displayed
 *     count matches the canonical `upcomingVisits` KPI counter.
 *
 * The Calendar page remains untouched for planning/scheduling — reachable
 * via the "View full calendar" secondary link.
 */
const STATUS_STYLE = {
    scheduled:   { label: "Scheduled",   dot: "#3B82F6", bg: "#EFF6FF", fg: "#1D4ED8" },
    confirmed:   { label: "Confirmed",   dot: "#3E9E2E", bg: "#F1F8EC", fg: "#166534" },
    in_progress: { label: "In progress", dot: "#F59E0B", bg: "#FEF3C7", fg: "#B45309" },
};

function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}
function fmtTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}
function fmtTimeRange(a) {
    const start = a.start_at || a.scheduled_at;
    const end = a.end_at;
    if (!start) return a.arrival_window || "—";
    if (!end) return fmtTime(start);
    return `${fmtTime(start)} – ${fmtTime(end)}`;
}

/** Group appointments into day buckets keyed by YYYY-MM-DD (chronological). */
function groupByDay(appts) {
    const buckets = new Map();
    for (const a of appts) {
        const iso = a.start_at || a.scheduled_at;
        if (!iso) continue;
        const d = new Date(iso);
        if (isNaN(d.getTime())) continue;
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
        if (!buckets.has(key)) buckets.set(key, { date: d, items: [] });
        buckets.get(key).items.push(a);
    }
    return Array.from(buckets.values()).sort((a, b) => a.date - b.date);
}

export default function InstallerUpcomingVisits() {
    const navigate = useNavigate();
    const [engineerId, setEngineerId] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Live "now" — captured once when the page loads so the list is stable
    // across renders. Any refresh reruns the effect and recomputes.
    const [nowIso] = useState(() => new Date().toISOString());

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const wl = await api.myWorkload();
                if (cancelled) return;
                const eid = wl?.engineer_id || null;
                setEngineerId(eid);
                if (!eid) { setAppointments([]); setLoading(false); return; }
                // Canonical: server filters by engineer + from-window.
                const rows = await api.listCalendarAppointments({
                    engineer_id: eid,
                    from: nowIso,
                });
                if (cancelled) return;
                // Mirror Mobile's client-side rules exactly so the list count
                // matches the canonical KPI: exclude cancelled/completed,
                // keep only start >= now, sort chronologically.
                const now = new Date(nowIso);
                const upcoming = (rows || [])
                    .filter((a) => a.status !== "cancelled" && a.status !== "completed")
                    .filter((a) => {
                        const t = a.start_at || a.scheduled_at;
                        return t && new Date(t) >= now;
                    })
                    .sort((a, b) => new Date(a.start_at || a.scheduled_at) - new Date(b.start_at || b.scheduled_at));
                setAppointments(upcoming);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Could not load upcoming visits");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [nowIso]);

    const days = useMemo(() => groupByDay(appointments), [appointments]);

    const openVisit = (a) => {
        // Route into the project workspace with the Scheduled Visits section
        // anchored (#visits) and the specific visit id passed through so the
        // section can highlight the row. Matches Mobile's drill-down intent.
        navigate(`/installer/project/${a.project_id}?visit=${a.id}#visits`);
    };

    const viewFullCalendar = () => {
        // Guard against clicking before workload resolves — without an
        // engineer_id the Calendar wouldn't be pre-filtered.
        if (loading || !engineerId) return;
        navigate(`/installer/calendar?engineer_id=${engineerId}`);
    };

    return (
        <InstallerShell>
            <PageContainer testid="upcoming-visits-page">
                <PageHeader
                    eyebrow="Engineer"
                    title="Upcoming visits"
                    subtitle="Every non-cancelled visit assigned to you, chronological, starting from now."
                    icon={CalendarClock}
                    backFallback="/installer"
                    testid="upcoming-visits-header"
                    rightSlot={
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={viewFullCalendar}
                                disabled={loading || !engineerId}
                                data-testid="upcoming-view-full-calendar"
                                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium bg-[#131B2F] text-white hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <CalendarIcon size={14} strokeWidth={2.25} /> View full calendar
                            </button>
                        </div>
                    }
                />

                {loading ? (
                    <div className="py-16 text-center text-sm text-[#4B5565]" data-testid="upcoming-loading">
                        Loading upcoming visits…
                    </div>
                ) : error ? (
                    <div className="pe-card p-8 text-center" data-testid="upcoming-error">
                        <div className="text-lg text-[#131B2F] font-medium">Couldn't load your visits</div>
                        <div className="text-sm text-[#4B5565] mt-1">{error}</div>
                    </div>
                ) : !engineerId ? (
                    <div className="pe-card p-12 flex flex-col items-center gap-3 text-center" data-testid="upcoming-no-identity">
                        <span className="w-14 h-14 rounded-full bg-[#FEF3F2] text-[#B42318] flex items-center justify-center">
                            <User size={26} strokeWidth={1.75} />
                        </span>
                        <div className="text-lg text-[#131B2F] font-medium">No engineer record linked</div>
                        <div className="text-sm text-[#4B5565] max-w-md">
                            Your sign-in isn't linked to an engineer in the roster. Ask Operations to link your record so your visits appear here.
                        </div>
                    </div>
                ) : appointments.length === 0 ? (
                    <div className="pe-card p-12 flex flex-col items-center gap-3 text-center" data-testid="upcoming-empty">
                        <span className="w-14 h-14 rounded-full bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center">
                            <CalendarClock size={26} strokeWidth={1.75} />
                        </span>
                        <div className="text-lg text-[#131B2F] font-medium">No upcoming visits</div>
                        <div className="text-sm text-[#4B5565] max-w-sm">
                            You have no future visits assigned to you. Head to the full calendar to plan or book new visits.
                        </div>
                        <button
                            type="button"
                            onClick={viewFullCalendar}
                            data-testid="upcoming-empty-full-calendar"
                            className="mt-3 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium bg-[#131B2F] text-white hover:brightness-110 transition-all"
                        >
                            <CalendarIcon size={14} strokeWidth={2.25} /> View full calendar
                        </button>
                    </div>
                ) : (
                    <div className="space-y-6" data-testid="upcoming-visits-list">
                        <div className="text-sm text-[#4B5565]" data-testid="upcoming-count">
                            Showing <span className="font-medium text-[#131B2F]">{appointments.length}</span>{" "}
                            {appointments.length === 1 ? "visit" : "visits"} · non-cancelled, non-completed, from now onwards.
                        </div>
                        {days.map(({ date, items }) => (
                            <section key={date.toISOString()} data-testid={`upcoming-day-${date.toISOString().slice(0,10)}`}>
                                <h2 className="text-sm font-medium text-[#4B5565] uppercase tracking-[0.1em] mb-2">
                                    {fmtDate(items[0].start_at || items[0].scheduled_at)}
                                </h2>
                                <ul className="space-y-2">
                                    {items.map((a) => {
                                        const st = STATUS_STYLE[a.status] || { label: a.status, dot: "#94A3B8", bg: "#F1F5F9", fg: "#334155" };
                                        const address = a.project_address || a.location || "—";
                                        const mapAddress = resolveMapAddress({ visit: a });
                                        return (
                                            <li key={a.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => openVisit(a)}
                                                    data-testid={`upcoming-visit-${a.id}`}
                                                    className="w-full text-left pe-card p-4 flex items-center gap-4 hover:bg-[#F9FAFB] transition-colors group"
                                                >
                                                    <div className="shrink-0 w-16 text-center">
                                                        <div className="text-xs text-[#4B5565]">{fmtTime(a.start_at || a.scheduled_at)}</div>
                                                        <div className="text-[10px] text-[#4B5565] mt-0.5">{fmtTimeRange(a).split("–")[1]?.trim() || ""}</div>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-base text-[#131B2F] font-medium truncate">
                                                                {a.customer_name || "—"}
                                                            </span>
                                                            <span
                                                                className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full"
                                                                style={{ backgroundColor: st.bg, color: st.fg }}
                                                            >
                                                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: st.dot }} />
                                                                {st.label}
                                                            </span>
                                                        </div>
                                                        <div className="text-sm text-[#4B5565] mt-0.5 truncate">
                                                            {a.appointment_type || a.title || "Visit"}
                                                            {a.arrival_window ? ` · ${a.arrival_window}` : ""}
                                                        </div>
                                                        <div className="flex items-center gap-1 text-xs text-[#4B5565] mt-1">
                                                            <MapPin size={12} strokeWidth={1.75} className="shrink-0" />
                                                            <span className="truncate">{address}</span>
                                                        </div>
                                                    </div>
                                                    <div className="shrink-0 flex items-center gap-1.5">
                                                        {(a.appointment_type || a.title || "").toLowerCase().includes("site survey") && (() => {
                                                            // DEV-010H post-UAT · Show the associated
                                                            // survey's actual status alongside the
                                                            // visit action. `a.survey_status` is
                                                            // populated by the calendar backend from
                                                            // the linked survey record.
                                                            const st = String(a.survey_status || "not_started").toLowerCase();
                                                            const READ = ["submitted","approved"].includes(st);
                                                            const CLAR = st === "returned_for_clarification";
                                                            const label = READ
                                                                ? (st === "approved" ? "Survey approved · View" : "Survey submitted · View")
                                                                : CLAR
                                                                    ? "Clarification required · Amend"
                                                                    : (st === "in_progress" ? "Continue site survey" : "Open site survey");
                                                            return (
                                                                <span
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={(e) => { e.stopPropagation(); navigate(`/installer/surveys/${a.project_id}?appointment_id=${a.id}`); }}
                                                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); navigate(`/installer/surveys/${a.project_id}?appointment_id=${a.id}`); } }}
                                                                    data-testid={`upcoming-open-survey-${a.id}`}
                                                                    data-survey-status={st}
                                                                    title={label}
                                                                    className={"inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-xs font-medium transition-colors cursor-pointer " +
                                                                        (READ
                                                                            ? "bg-white border border-[#3E9E2E] text-[#166534] hover:bg-[#F1F8EC]"
                                                                            : CLAR
                                                                                ? "bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B] hover:brightness-95"
                                                                                : "bg-[#131B2F] text-white hover:brightness-110")}
                                                                >
                                                                    <ClipboardList size={12} strokeWidth={2} /> {label}
                                                                </span>
                                                            );
                                                        })()}
                                                        {mapAddress && (
                                                            <span
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={(e) => { e.stopPropagation(); openDirections(mapAddress); }}
                                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); openDirections(mapAddress); } }}
                                                                data-testid={`upcoming-directions-${a.id}`}
                                                                title={`Directions to ${mapAddress}`}
                                                                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-xs font-medium bg-white border border-[#E5E7EB] text-[#131B2F] hover:bg-[#131B2F] hover:text-white transition-colors cursor-pointer"
                                                            >
                                                                <Navigation size={12} strokeWidth={2} /> Directions
                                                            </span>
                                                        )}
                                                        <ChevronRight size={18} className="text-[#94A3B8] group-hover:text-[#131B2F] transition-colors" />
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        ))}
                    </div>
                )}
            </PageContainer>
        </InstallerShell>
    );
}
